import { condicionSeCumple } from './definicion.js';
import { respuestaEsValida } from './tipos.js';
/**
 * Preguntas que corresponde **mostrar** con lo respondido hasta ahora.
 *
 * Una condición sin resolver no habilita: hasta que el paciente no conteste si
 * camina por su casa, las preguntas que dependen de eso no se muestran. Es la
 * semántica de `enableWhen` de FHIR y la que hace que el recorrido avance de a
 * una pantalla.
 */
export function preguntasActivas(formulario, respuestas) {
    return formulario.preguntas.filter((pregunta) => (pregunta.cuando ?? []).every((condicion) => condicionSeCumple(condicion, respuestas)));
}
/**
 * Preguntas que **todavía pueden aparecer**: las activas más las que dependen de
 * algo que aún no se contestó.
 *
 * Es distinto de `preguntasActivas` y la diferencia importa sólo para contar. Al
 * abrir el NMS ninguna condición está resuelta, así que hay una sola pregunta
 * activa; mostrar "Pregunta 1 de 1" y saltar a "2 de 3" al contestar sería
 * mentirle al paciente sobre cuánto falta. El total baja **cuando se sabe** que
 * baja: recién cuando contesta que no camina por su casa, el formulario dice que
 * era una sola.
 */
export function preguntasPosibles(formulario, respuestas) {
    return formulario.preguntas.filter((pregunta) => !(pregunta.cuando ?? []).some((condicion) => respuestas[condicion.pregunta] !== undefined && !condicionSeCumple(condicion, respuestas)));
}
/** La que toca ahora, o `null` si no queda ninguna. */
export function siguientePregunta(formulario, respuestas) {
    return (preguntasActivas(formulario, respuestas).find((pregunta) => respuestas[pregunta.id] === undefined) ?? null);
}
export function progresoDel(formulario, respuestas) {
    const posibles = preguntasPosibles(formulario, respuestas);
    const respondidas = posibles.filter((pregunta) => respuestas[pregunta.id] !== undefined).length;
    return {
        numero: Math.min(respondidas + 1, posibles.length),
        total: posibles.length,
        respondidas,
    };
}
/** Todas las activas respondidas: el formulario está para cerrar. */
export function estaCompleto(formulario, respuestas) {
    return siguientePregunta(formulario, respuestas) === null;
}
/**
 * Respuestas que sobran porque su pregunta dejó de estar activa.
 *
 * Pasa de verdad: el paciente contesta que sale al barrio y después vuelve
 * atrás y corrige que no camina en casa. Las respuestas de las preguntas que se
 * apagaron **no se borran acá** —esta función es pura y sólo las nombra— pero
 * tampoco se puntúan ni viajan a FHIR: la escala tiene que decir lo mismo que
 * dijo el paciente al final, no la suma de todo lo que tipeó en el camino.
 */
export function respuestasHuerfanas(formulario, respuestas) {
    const activas = new Set(preguntasActivas(formulario, respuestas).map((pregunta) => pregunta.id));
    return Object.keys(respuestas).filter((id) => !activas.has(id));
}
/** Se queda con lo respondido a preguntas que siguen activas. */
export function respuestasVigentes(formulario, respuestas) {
    const huerfanas = new Set(respuestasHuerfanas(formulario, respuestas));
    return Object.fromEntries(Object.entries(respuestas).filter(([id]) => !huerfanas.has(id)));
}
/**
 * Verifica el conjunto de respuestas contra la definición.
 *
 * Tres cosas distintas, que conviene no mezclar: una respuesta a una pregunta
 * que no existe es un cliente desactualizado o malicioso; una a una pregunta que
 * la skip-logic apagó es un recorrido inconsistente; y un valor fuera del campo
 * es lo único que un formulario bien hecho no debería producir nunca. Las tres
 * se rechazan en la API — Zod estricto, sin datos sin tipar hacia la lógica.
 *
 * El `contexto` es lo que el rango de una fecha y un código de catálogo
 * necesitan saber (E3.25): sin él se valida la forma, con él también el rango.
 */
export function problemasDeLasRespuestas(formulario, respuestas, contexto = {}) {
    const problemas = [];
    const porId = new Map(formulario.preguntas.map((pregunta) => [pregunta.id, pregunta]));
    const activas = new Set(preguntasActivas(formulario, respuestas).map((pregunta) => pregunta.id));
    for (const [id, valor] of Object.entries(respuestas)) {
        const pregunta = porId.get(id);
        if (!pregunta) {
            problemas.push({ pregunta: id, motivo: 'pregunta-desconocida' });
            continue;
        }
        if (!activas.has(id)) {
            problemas.push({ pregunta: id, motivo: 'pregunta-inactiva' });
            continue;
        }
        if (!respuestaEsValida(pregunta.campo, valor, contexto)) {
            problemas.push({ pregunta: id, motivo: 'valor-invalido' });
        }
    }
    return problemas;
}
//# sourceMappingURL=recorrido.js.map